import { useState, useCallback } from 'react'
import { Box, Stack, SimpleGrid, Text, Badge, Group, Table, Skeleton } from '@mantine/core'
import {
  IconServer,
  IconTrendingUp,
  IconAlertTriangle,
} from '@tabler/icons-react'
import type { TopologyNode } from '@/api/topology'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useCurrentTenant } from '@/store/auth'
import { SmallBox, WidgetCard, CardHeader, DataCard, Callout } from '@/components/ui'
import {
  HealthScoreGauge,
  NodeDrawer,
  TopologyCanvas,
  TopologySearch,
  IncidentsTimeline,
  NetworkFlow,
  PerformanceCharts,
  ResourceTabs,
  CapacityForecast,
} from '@/components/topology'
import { useDisclosure } from '@mantine/hooks'
import { Collapse, Button } from '@mantine/core'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { ZabbixOverview } from '@/pages/dashboard/ZabbixOverview'
import { ZabbixHostsTable } from '@/pages/dashboard/ZabbixHostsTable'
import { ZabbixProblemsPanel } from '@/pages/dashboard/ZabbixProblemsPanel'
import { useZabbixMetrics } from '@/hooks/useZabbixMetrics'
import { useTopologyData } from '@/hooks/useTopologyData'

/**
 * S3 buckets are not part of the topology graph (they connect to nothing in
 * ALB→EC2→RDS), so `build_topology` never emits them. To populate the S3 tab
 * we reuse the existing, cached `/inventory/s3` endpoint (same one the Recursos
 * screen uses) and map its buckets into the node shape ResourceTabs expects.
 * TASK-2604.
 */
interface S3InventoryBucket {
  name: string
  region: string
  versioning: string
}

export function s3BucketsToNodes(buckets: S3InventoryBucket[] | undefined): TopologyNode[] {
  return (buckets ?? []).map((b) => ({
    id: `s3-${b.name}`,
    data: {
      label: b.name,
      type: 's3' as const,
      status: 'healthy' as const,
      region: b.region,
      versioning: b.versioning,
    },
    position: { x: 0, y: 0 },
  }))
}

/**
 * Infrastructure Tab Component — Command Center Layout (TASK-1371)
 */
export function InfrastructureTab() {
  const { kpis } = useDashboardData('dev')
  const tenant = useCurrentTenant()

  const auditLog = useQuery({
    queryKey: ['audit-infra', tenant?.id],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenant!.id}/audit?limit=8`)
      return res.data
    },
    enabled: !!tenant?.id,
    staleTime: 60_000,
  })

  // CloudWatch metrics (TASK-1355)
  const cwMetrics = useQuery({
    queryKey: ['infra-metrics', tenant?.id],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenant!.id}/infrastructure/metrics`)
      return res.data
    },
    enabled: !!tenant?.id,
    staleTime: 300_000, // 5min — backend also caches 5min
  })

  // TODO: multi-DB selector
  const rdsList = useQuery({
    queryKey: ['rds-list', tenant?.id],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenant!.id}/rds`)
      return res.data
    },
    enabled: !!tenant?.id,
    staleTime: 300_000,
  })

  // S3 inventory (TASK-2604) — reuses the same query key as the Recursos screen
  // so the cached result is shared instead of fetched twice.
  const s3Inventory = useQuery({
    queryKey: ['inventory-s3', tenant?.id],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenant!.id}/inventory/s3`)
      return res.data as { buckets: S3InventoryBucket[] }
    },
    enabled: !!tenant?.id,
    staleTime: 300_000,
  })

  const { topology: topologyQuery, incidents, incidentsToday } = useTopologyData()

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedNodeData, setSelectedNodeData] = useState<TopologyNode['data'] | null>(null)

  const handleNodeClick = useCallback((nodeId: string, nodeData: TopologyNode['data']) => {
    setSelectedNodeId(nodeId)
    setSelectedNodeData(nodeData)
  }, [])

  const handleSearchSelect = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId)
    const node = topologyQuery.data?.nodes.find(n => n.id === nodeId)
    if (node) setSelectedNodeData(node.data)
  }, [topologyQuery.data])

  const resources = kpis.data?.resources
  const alerts = kpis.data?.alerts
  const ec2Data = resources?.by_type?.ec2 as any

  const cw = cwMetrics.data
  const cwError = cwMetrics.isError

  return (
    <Stack gap="xl">
      {/* Row 1: Summary KPI cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <SmallBox
          value={resources?.total ?? '—'}
          label="Recursos Ativos"
          icon={<IconServer size={40} />}
          color="info"
          linkLabel="Ver recursos"
          linkTo="/recursos"
          loading={kpis.isLoading}
          info="Total de recursos AWS provisionados para este tenant: EC2, ALB, RDS, Target Groups, domínios."
        />
        <SmallBox
          value={cw?.alarms?.length ?? alerts?.total ?? 0}
          label="Alarmes Ativos"
          icon={<IconAlertTriangle size={40} />}
          color={cw?.alarms?.length > 0 ? 'danger' : 'primary'}
          loading={kpis.isLoading}
          info="Alarmes CloudWatch em estado ALARM agora. Inclui alertas de CPU, memória, 5XX e status check. Zero = ambiente saudável."
        />
        <SmallBox
          value={ec2Data?.running ?? '—'}
          label="EC2 Rodando"
          icon={<IconServer size={40} />}
          color="success"
          loading={kpis.isLoading}
          info="Instâncias EC2 com state=running. Instâncias stopped ou terminated não são contadas."
        />
        <SmallBox
          value={cw?.ec2?.status_check_failed === 0 ? '100%' : `${Math.round(((ec2Data?.running ?? 0) / Math.max(ec2Data?.total ?? 1, 1)) * 100)}%`}
          label="Uptime"
          icon={<IconTrendingUp size={40} />}
          color={cw?.ec2?.status_check_failed > 0 ? 'danger' : 'success'}
          loading={kpis.isLoading}
          info="Se nenhuma instância falhou no StatusCheck do CloudWatch: 100%. Caso contrário: (running / total) × 100. Abaixo de 100% indica instância com problema."
        />
      </SimpleGrid>

      {/* Zabbix Monitoring Section (TASK-1919) */}
      {tenant?.id && (
        <ZabbixSection tenantId={String(tenant.id)} />
      )}

      {/* Row 2: Network Flow */}
      {cwError && (
        <Callout variant="warning" title="Métricas CloudWatch">
          <Text size="sm">Métricas indisponíveis para este tenant. Verifique as permissões da IAM role.</Text>
        </Callout>
      )}

      <NetworkFlow
        nodes={topologyQuery.data?.nodes ?? []}
        cwMetrics={cwMetrics.data}
        loading={topologyQuery.isLoading}
      />

      {/* Row 3: Performance Charts */}
      <PerformanceCharts
        cwMetrics={cwMetrics.data}
        loading={cwMetrics.isLoading}
      />

      {/* Row 3b: RDS Capacity Forecast */}
      {rdsList.data?.instances?.[0] && (
        <CapacityForecast dbIdentifier={rdsList.data.instances[0].db_identifier} />
      )}

      {/* Row 4: Health Score + Topology Canvas */}
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <WidgetCard accent="info" p={0}>
          <CardHeader title="Health Score" />
          <Box p="md">
            <HealthScoreGauge
              score={topologyQuery.data?.health_score}
              nodes={topologyQuery.data?.nodes ?? []}
              incidents={incidentsToday.data?.incidents ?? []}
              loading={topologyQuery.isLoading}
            />
          </Box>
        </WidgetCard>

        <WidgetCard accent="primary" p={0}>
          <CardHeader
            title="Topologia de Infraestrutura"
            badge={topologyQuery.data?.nodes?.length ? { value: `${topologyQuery.data.nodes.length} recursos`, color: 'blue' } : undefined}
          />
          <Box p="md" pt={0}>
            {topologyQuery.data && topologyQuery.data.nodes.length > 0 && (
              <Box mb="sm">
                <TopologySearch
                  nodes={topologyQuery.data.nodes}
                  onSelect={handleSearchSelect}
                />
              </Box>
            )}
            <TopologyCanvas
              nodes={topologyQuery.data?.nodes}
              edges={topologyQuery.data?.edges}
              loading={topologyQuery.isLoading}
              onNodeClick={handleNodeClick}
            />
          </Box>
        </WidgetCard>
      </SimpleGrid>

      {/* Row 5: Resource Tabs */}
      <WidgetCard accent="primary" p={0}>
        <CardHeader title="Recursos por Tipo" />
        <Box p="md">
          <ResourceTabs
            topologyNodes={[
              ...(topologyQuery.data?.nodes ?? []),
              ...s3BucketsToNodes(s3Inventory.data?.buckets),
            ]}
            cwMetrics={cwMetrics.data}
            onResourceClick={handleNodeClick}
            loading={topologyQuery.isLoading || s3Inventory.isLoading}
          />
        </Box>
      </WidgetCard>

      {/* Row 6: Incidents Timeline */}
      {incidents.data && (
        <WidgetCard accent="warning" p={0}>
          <CardHeader title="Timeline de Incidentes" />
          <Box p="md">
            <IncidentsTimeline incidents={incidents.data.incidents} />
          </Box>
        </WidgetCard>
      )}

      {/* Row 7: CloudWatch Alarms */}
      {cw?.alarms && cw.alarms.length > 0 && (
        <DataCard flush title="Alarmes CloudWatch Ativos" accent="danger">
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Estado</Table.Th>
                <Table.Th>Métrica</Table.Th>
                <Table.Th>Desde</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {cw.alarms.map((alarm: any, i: number) => (
                <Table.Tr key={i}>
                  <Table.Td><Text size="sm" fw={500}>{alarm.name}</Text></Table.Td>
                  <Table.Td><Badge size="sm" color="red">ALARM</Badge></Table.Td>
                  <Table.Td><Badge size="xs" variant="light">{alarm.metric || alarm.namespace}</Badge></Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {alarm.since_seconds != null
                        ? alarm.since_seconds < 3600 ? `${Math.round(alarm.since_seconds / 60)} min`
                        : `${Math.round(alarm.since_seconds / 3600)}h`
                        : '—'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </DataCard>
      )}

      {/* Row 8: Audit Log */}
      <DataCard flush title="Audit Log — Eventos Recentes" accent="warning" refreshable onRefresh={() => auditLog.refetch()} loading={auditLog.isLoading}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Data</Table.Th>
              <Table.Th>Ação</Table.Th>
              <Table.Th>Recurso</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(auditLog.data?.items ?? []).length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text ta="center" c="dimmed" py="md">Nenhum evento recente</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              (auditLog.data?.items ?? []).map((e: any, i: number) => (
                <Table.Tr key={e.id ?? i}>
                  <Table.Td><Text size="sm" c="dimmed">{e.created_at ? new Date(e.created_at).toLocaleString('pt-BR') : '—'}</Text></Table.Td>
                  <Table.Td><Text size="sm" fw={500}>{e.action ?? e.event_type ?? '—'}</Text></Table.Td>
                  <Table.Td><Badge size="xs" variant="light">{e.resource_type ?? '—'}</Badge></Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={e.status === 'success' ? 'green' : 'red'}>
                      {e.status ?? '—'}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </DataCard>

      {/* NodeDrawer */}
      <NodeDrawer
        opened={selectedNodeId !== null}
        onClose={() => { setSelectedNodeId(null); setSelectedNodeData(null) }}
        nodeId={selectedNodeId}
        nodeData={selectedNodeData}
      />
    </Stack>
  )
}

function ZabbixSection({ tenantId }: { tenantId: string }) {
  const { data, isLoading } = useZabbixMetrics(tenantId)
  const [opened, { toggle }] = useDisclosure(true)

  if (isLoading) return null
  if (data === undefined) return null

  return (
    <WidgetCard accent="info" p={0}>
      <Group justify="space-between" p="md" pb={0}>
        <CardHeader title="Monitoramento Zabbix" />
        <Button
          variant="subtle"
          size="xs"
          onClick={toggle}
          rightSection={opened ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
        >
          {opened ? 'Recolher' : 'Expandir'}
        </Button>
      </Group>
      <Collapse in={opened}>
        <Stack gap="md" p="md">
          <ZabbixOverview tenantId={tenantId} />
          <ZabbixHostsTable tenantId={tenantId} />
          <ZabbixProblemsPanel tenantId={tenantId} />
        </Stack>
      </Collapse>
    </WidgetCard>
  )
}
